import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';

/**
 * Stacked Column Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-stacked-column-graph',
  standalone: false,
  templateUrl: './stacked-column-graph.component.html',
  styleUrl: './stacked-column-graph.component.scss'
})
export class StackedColumnGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private stackedColumnGraphReportSubscription: any;
  private isStackedColumnViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.stackedColumnGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Stacked Column Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isStackedColumnViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });

    });
  }

  ngAfterViewInit(): void {
    this.isStackedColumnViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('stacked-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Stacked Column Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isStackedColumnViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('stacked-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Stacked Column Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('stacked-column-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Create chart 
   * @param data - Chart data
   */
  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Stacked Column Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('stacked-column-chartdiv');
    if (!chartContainer) {
      console.error('Stacked Column Chart - Chart container not found! Make sure element with id "stacked-column-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Stacked Column Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Stacked Column Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('stacked-column-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'stacked-column',
        data: data
      });
      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      const chart = this._baseChartService.createChartContainer(this.root, {
        layout: this.root.verticalLayout
      });

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(chart, 'slide');

      this._baseChartService.setupColorPalette(chart);

      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 20,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      // Enhanced axis animation
      this._baseChartService.setupAxisAnimation(categoryAxis);

      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y'
      });

      // Enhanced axis animation for value axis
      this._baseChartService.setupAxisAnimation(valueAxis);

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((valueField) => {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(this.root, {
            name: valueField,
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            stacked: true
          })
        );

        // Enhanced series animation
        this._baseChartService.setupSeriesAnimation(series, 'elastic');

        // Set enhanced tooltip with value and label display
        // 🔥 FIX: tooltip only for this series
        series.columns.template.set(
          "tooltipText",
          "{name}: {valueY}"
        );
        //series.set('tooltip', tooltip);

        // Set data
        series.data.setAll(data);
      });

      // Add cursor
      chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: categoryAxis
      }));

      // Add legend
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      // Configure legend
      legend.set('maxHeight', 40);

      // Configure legend markers
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Set legend data
      legend.data.setAll(chart.series.values);

      // Chart is ready - no animation trigger needed

      console.log('Stacked Column Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating stacked column chart:', error);
      this.isCreatingChart = false;
    }
  }

  /**
   * Update the chart with new data
   * @param data - New data to display
   */
  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Stacked Column Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('stacked-column-chartdiv');
    if (!chartContainer) {
      console.log('Stacked Column Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Stacked Column Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Hide spinner if it's still showing
    this._spinnerService.hide();
    this._baseChartService.disposeChart(this.root);
    if (this.stackedColumnGraphReportSubscription) {
      this.stackedColumnGraphReportSubscription.unsubscribe();
    }
  }
}
